import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "About Us", id: "about" },
  { label: "Properties", to: "/properties" },
  { label: "Investment Opportunities", id: "investment" },
  { label: "Locations", id: "locations" },
  { label: "Why Invest", id: "why-choose" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onDark = location.pathname === "/" && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleNav = (link) => {
    setOpen(false);
    if (link.to) {
      navigate(link.to);
      return;
    }
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: link.id } });
    else scrollToId(link.id);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "border-b border-gray-100 bg-white/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => handleNav({ id: "home" })}
          className={`flex items-center gap-3 text-left ${onDark ? "text-white" : "text-navy"}`}
          aria-label="Exserviceman Property Services - Home"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-gold text-navy">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-tight">Exserviceman</span>
            <span className={`block text-[10px] font-semibold uppercase tracking-[0.28em] ${onDark ? "text-gold-light" : "text-gold-dark"}`}>
              Property Services
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <button
              key={link.label}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(link)}
              className={`text-sm font-medium transition-colors duration-200 hover:text-gold ${
                onDark ? "text-white/85" : "text-navy/80"
              }`}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/properties"
            data-testid="nav-view-plots-button"
            className="rounded-sm bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-gold-light"
          >
            View Available Plots
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden ${onDark ? "text-white" : "text-navy"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-gray-100 bg-white/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((link) => (
                <button
                  key={link.label}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => handleNav(link)}
                  className="block w-full rounded-sm px-3 py-3 text-left text-sm font-medium text-navy hover:bg-navy-50"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/properties"
                data-testid="mobile-nav-view-plots-button"
                className="mt-2 block rounded-sm bg-gold px-3 py-3 text-center text-sm font-semibold text-navy"
              >
                View Available Plots
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
